import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, UsersThree, Link as LinkIcon } from "@phosphor-icons/react";

export default function Referrals() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/referrals/tree").then(({ data }) => setData(data));
  }, []);

  if (!data) {
    return <div className="p-8 text-white/60">Loading…</div>;
  }

  const referralLink = `${window.location.origin}/?ref=${data.referral_code}`;

  const copy = async () => {
    await navigator.clipboard.writeText(referralLink);
    toast.success("Referral link copied!");
  };

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto space-y-6" data-testid="referrals-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-black">Referrals</h1>
        <p className="text-white/60 mt-1">
          Earn 10% from your invites, 2% from their invites, 1% from theirs. Deducted from their earnings.
        </p>
      </div>

      {/* Referral link card */}
      <Card className="bg-[#121212] border-white/10">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <LinkIcon size={20} weight="bold" /> Your referral link
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <div className="flex-1 px-4 py-3 rounded-md bg-white/5 border border-white/10 text-sm font-mono truncate">
              {referralLink}
            </div>
            <Button
              data-testid="copy-referral-btn"
              onClick={copy}
              className="bg-blue-600 hover:bg-blue-700 gap-2"
            >
              <Copy size={16} weight="bold" /> Copy
            </Button>
          </div>
          <p className="text-xs text-white/50">Your code: <span className="font-mono text-white">{data.referral_code}</span></p>
        </CardContent>
      </Card>

      {/* Earnings summary */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "L1 earnings (10%)", value: data.earnings.l1, sub: `${data.generation_1.length} invites` },
          { label: "L2 earnings (2%)", value: data.earnings.l2, sub: `${data.generation_2.length} people` },
          { label: "L3 earnings (1%)", value: data.earnings.l3, sub: `${data.generation_3.length} people` },
        ].map((s, i) => (
          <Card key={i} className="bg-[#121212] border-white/10">
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-widest font-bold text-white/50">{s.label}</p>
              <p className="font-heading text-3xl font-black text-emerald-400 mt-1">
                ${s.value.toFixed(2)}
              </p>
              <p className="text-xs text-white/40 mt-1">{s.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tree */}
      {[
        { title: "Direct Invites (Level 1)", users: data.generation_1 },
        { title: "Generation 2", users: data.generation_2 },
        { title: "Generation 3", users: data.generation_3 },
      ].map((section, i) => (
        <Card key={i} className="bg-[#121212] border-white/10">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <UsersThree size={20} weight="bold" /> {section.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {section.users.length === 0 ? (
              <p className="text-white/50 text-sm">No one here yet.</p>
            ) : (
              <div className="space-y-2">
                {section.users.map((u) => (
                  <div key={u.user_id} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-b-0">
                    {u.picture ? (
                      <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-white/10" />
                    )}
                    <p className="flex-1 font-semibold text-sm truncate">{u.name}</p>
                    <p className="text-xs text-white/50">${u.balance.toFixed(2)} earned</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
